import { startCase } from 'lodash'
import React from 'react'
import intl from 'react-intl-universal'
import { ValueType } from 'react-select'
import { connect } from 'react-redux'
import { compose, pure, withHandlers, withState } from 'recompose'
import { MediaType } from '../types'
import { FeaturedMediaSelectorSFC } from './featuredMediaSelector.sfc'
import {
  HandlerProps,
  IFeaturedMediaSelectorProps,
  IOptionType,
  IOuterProps,
} from './types'
import { IEllipsisReduxStore } from '~/redux/types'
import { getCustomEmbed } from '~/redux/settingsReducer.js'
import { getBuiltinTypes } from '../builtinTypes'
import { getKey } from '~/util/customEmbed'
import { getEvidentlyFeaturesSync } from '~/evidentlyFeatureFlags'

const KEY_ALREADY_EXISTS = intl.get('settings.featuredMedia.keyExistsError')
const REQUIRED_FIELD = intl.get('settings.featuredMedia.requiredFieldError')

export const _mapStateToProps = (store: IEllipsisReduxStore) => {
  const customEmbed = getCustomEmbed(store)
  const customEmbedOptions = Object.keys(customEmbed).map((key) => ({
    label: customEmbed[key].caption || getKey(key),
    value: getKey(key),
  }))

  const evidentlyFeatures = getEvidentlyFeaturesSync()

  const builtinTypesList = getBuiltinTypes(evidentlyFeatures)
  const defaultOptions = builtinTypesList.map((value) => ({
    label: intl.get(`settings.featuredMedia.${value}`),
    value,
  }))

  return {
    selectOptions: [...defaultOptions, ...customEmbedOptions],
  }
}

export const _handlers = {
  onMediaKeyChange: (props: HandlerProps) => (
    event: React.SyntheticEvent<HTMLInputElement>
  ) => {
    props.setMediaKey(event.currentTarget.value)
    props.setMediaKeyValidationError()
  },
  onMediaTypesChange: (props: HandlerProps) => (
    selected: ValueType<IOptionType>
  ) => {
    props.setMediaTypes(selected)
    props.setMediaTypesValidationError()
  },
  onSubmit: (props: HandlerProps) => (
    event: React.SyntheticEvent<HTMLInputElement>
  ) => {
    event.preventDefault()
    if (
      !props.mediaTypes ||
      !('length' in props.mediaTypes) ||
      props.mediaTypes.length === 0
    ) {
      props.setMediaTypesValidationError(REQUIRED_FIELD)
      return
    }
    if (props.mediaKey.length === 0) {
      props.setMediaKeyValidationError(REQUIRED_FIELD)
      return
    } else if (!props.canAddKey(props.mediaKey)) {
      props.setMediaKeyValidationError(KEY_ALREADY_EXISTS)
      return
    }
    props.onAdd(props.mediaKey, {
      label: startCase(props.mediaKey),
      requiredToPublish: false,
      types: props.mediaTypes.map(
        ({ value, label }): MediaType => ({ key: value, label })
      ),
    })
    props.setMediaTypes([])
    props.setMediaKey('')
  },
}

const enhancer = compose<IFeaturedMediaSelectorProps, IOuterProps>(
  pure,
  connect(_mapStateToProps),
  // Validation flags
  withState('mediaTypesValidationError', 'setMediaTypesValidationError', ''),
  withState('mediaKeyValidationError', 'setMediaKeyValidationError', ''),
  // Form values
  withState('mediaTypes', 'setMediaTypes', []),
  withState('mediaKey', 'setMediaKey', ''),
  withHandlers(_handlers)
)

export const FeaturedMediaSelector = enhancer(FeaturedMediaSelectorSFC)
